<template>
  <div class="repair-slip-link">
    <!-- Không có liên kết: Bill thường (không sinh từ phiếu sửa chữa) -->
    <div v-if="!repairSlipId" class="no-slip">
      <h2 class="title">Hoá đơn không liên kết phiếu sửa chữa</h2>
      <p class="desc">
        Hoá đơn này được tạo trực tiếp (không phát sinh từ flow Dịch vụ bảo hành).
      </p>
      <img
        src="media/svg/illustrations/search.svg"
        class="mw-100 h-200px h-sm-325px searching-img"
        alt="no link"
      />
    </div>

    <!-- Đang load -->
    <div v-else-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2 text-muted">Đang tải dữ liệu phiếu sửa chữa...</p>
    </div>

    <!-- Lỗi -->
    <b-alert v-else-if="errorMsg" show variant="danger" class="m-3">
      <i class="fas fa-exclamation-circle mr-2"></i>
      {{ errorMsg }}
    </b-alert>

    <!-- Có liên kết: hiển thị summary phiếu SC + danh sách parts billable -->
    <div v-else-if="slip" class="trade-in-card">
      <h4 class="title">
        Phiếu sửa chữa liên kết
        <b-badge :variant="statusVariant(slip.status)" class="ml-2">
          {{ statusLabel(slip.status) }}
        </b-badge>
      </h4>
      <div class="head">
        <div class="code">
          <span class="card-label">Mã phiếu</span>
          <span class="card-content">#{{ slip.slipNumber }}</span>
        </div>
        <div class="created-date">
          <span class="card-label">Ngày tạo</span>
          <span class="card-content">{{ formatDateTime(slip.createdAt) }}</span>
        </div>
        <div class="created-by">
          <span class="card-label">Cửa hàng</span>
          <span class="card-content">{{ (slip.store && slip.store.name) || '—' }}</span>
        </div>
        <div class="stock-name">
          <span class="card-label">Loại bảo hành</span>
          <span class="card-content">{{ slip.warrantyProductName || '—' }}</span>
        </div>
        <div class="total-amount">
          <span class="card-label">Khách hàng</span>
          <span class="card-content">{{ slip.customerName }}</span>
          <span class="card-content text-muted small">{{ slip.customerPhone }}</span>
        </div>
        <div class="action">
          <b-button variant="primary" size="sm" @click="goToSlipDetail">
            <i class="fas fa-external-link-alt mr-1"></i>Xem chi tiết
          </b-button>
        </div>
      </div>

      <div class="note">
        <span class="card-label">Thiết bị</span>
        <p>
          {{ slip.productName || '—' }}
          <span v-if="slip.imei" class="text-muted small">— IMEI: {{ slip.imei }}</span>
          <b-badge v-if="slip.isExternalDevice" variant="warning" class="ml-1">
            Hàng ngoài
          </b-badge>
        </p>
      </div>

      <div class="list-product">
        <span class="card-label">Sản phẩm xuất hoá đơn (parts có "Xuất HĐ" = ON):</span>
        <table>
          <thead>
            <tr>
              <th>Loại</th>
              <th>Mã SP</th>
              <th>Tên sản phẩm</th>
              <th>SL</th>
              <th>Đơn giá</th>
              <th>VAT</th>
              <th>Thành tiền</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, idx) in billableParts" :key="idx">
              <td>
                <b-badge :variant="p.productType === 4 ? 'info' : 'primary'">
                  {{ p.productType === 4 ? 'Dịch vụ' : 'Linh kiện' }}
                </b-badge>
              </td>
              <td>{{ p.productCode || '—' }}</td>
              <td>{{ p.productName }}</td>
              <td>{{ p.quantity }}</td>
              <td>{{ formatPrice(p.price) }}</td>
              <td>{{ p.vatRate }}%</td>
              <td>
                <strong>{{ formatPrice(computeLineTotal(p)) }}</strong>
              </td>
            </tr>
            <tr v-if="billableParts.length === 0">
              <td colspan="7" class="text-center text-muted py-3">
                Phiếu không có sản phẩm nào "xuất hoá đơn".
              </td>
            </tr>
          </tbody>
          <tfoot v-if="billableParts.length > 0">
            <tr>
              <td colspan="6" class="text-right"><strong>Tổng cộng:</strong></td>
              <td>
                <strong class="text-primary">{{ formatPrice(totalBillable) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import moment from 'moment';

const SLIP_API = 'v2/warranty-service/repair-slips';
const PARTS_API = (id) => `v2/warranty-service/repair-slips/${id}/parts`;

const STATUS_LABELS = {
  receiving: { text: 'Đang tiếp nhận', variant: 'info' },
  checking: { text: 'Đang kiểm tra', variant: 'primary' },
  repairing: { text: 'Đang sửa chữa', variant: 'primary' },
  waiting_part: { text: 'Chờ linh kiện', variant: 'warning' },
  sent_to_vendor: { text: 'Gửi hãng', variant: 'warning' },
  completed: { text: 'Hoàn thành', variant: 'success' },
  notified: { text: 'Đã thông báo KH', variant: 'success' },
  returned: { text: 'Đã trả máy', variant: 'success' },
  rejected_quote: { text: 'KH từ chối báo giá', variant: 'secondary' },
  ber: { text: 'BER', variant: 'dark' },
  cancelled: { text: 'Đã huỷ', variant: 'danger' },
};

export default {
  name: 'RepairSlipLink',
  props: {
    /**
     * Bill.repairSlipId — UUID. Null nếu Bill không phát sinh từ phiếu sửa chữa.
     */
    repairSlipId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      slip: null,
      parts: [],
      loading: false,
      errorMsg: '',
    };
  },
  computed: {
    billableParts() {
      return this.parts.filter(
        (p) => Number(p.price) > 0 && Number(p.quantity) > 0,
      );
    },
    totalBillable() {
      return this.billableParts.reduce((sum, p) => sum + this.computeLineTotal(p), 0);
    },
  },
  watch: {
    repairSlipId: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchData();
        else {
          this.slip = null;
          this.parts = [];
        }
      },
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const [slipRes, partsRes] = await Promise.all([
          ApiService.query(`${SLIP_API}/${this.repairSlipId}`),
          ApiService.query(PARTS_API(this.repairSlipId)),
        ]);
        this.slip = (slipRes && slipRes.data && slipRes.data.data) || null;
        this.parts = (partsRes && partsRes.data && partsRes.data.data) || [];
      } catch (err) {
        this.errorMsg =
          err?.response?.data?.message
          || err?.message
          || 'Không tải được dữ liệu phiếu sửa chữa.';
      } finally {
        this.loading = false;
      }
    },
    goToSlipDetail() {
      // Mở chi tiết phiếu SC ở tab mới để không mất state Bill hiện tại
      const url = this.$router.resolve({
        name: 'edit-repair-slip',
        params: { id: this.repairSlipId },
      });
      window.open(url.href, '_blank');
    },
    statusLabel(value) {
      return (STATUS_LABELS[value] && STATUS_LABELS[value].text) || value;
    },
    statusVariant(value) {
      return (STATUS_LABELS[value] && STATUS_LABELS[value].variant) || 'secondary';
    },
    computeLineTotal(p) {
      const qty = Number(p.quantity) || 0;
      const price = Number(p.price) || 0;
      const vat = Number(p.vatRate) || 0;
      return qty * price * (1 + vat / 100);
    },
    formatPrice(value) {
      if (value === undefined || value === null) return '0';
      return Number(value).toLocaleString('vi-VN');
    },
    formatDateTime(value) {
      if (!value) return '';
      return moment(value).format('DD/MM/YYYY HH:mm');
    },
  },
};
</script>

<style lang="scss" scoped>
// Match style của TradeIn.vue để 2 tab nhìn nhất quán
.repair-slip-link {
  .no-slip {
    text-align: center;
    padding: 3rem 1rem;
    .title { font-size: 1.5rem; margin-bottom: 0.5rem; }
    .desc { color: #6c757d; margin-bottom: 1rem; }
  }
  .trade-in-card {
    padding: 1.5rem;
    .title {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 1rem;
      color: #181c32;
    }
    .head {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 1rem;
      align-items: start;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 0.5rem;
      margin-bottom: 1rem;
      > div {
        display: flex;
        flex-direction: column;
      }
      .card-label {
        font-size: 0.75rem;
        color: #6c757d;
        text-transform: uppercase;
        margin-bottom: 0.25rem;
      }
      .card-content {
        font-weight: 600;
        color: #181c32;
      }
      .action {
        justify-content: flex-end;
      }
    }
    .note {
      padding: 1rem;
      background: #fffbea;
      border-radius: 0.5rem;
      margin-bottom: 1rem;
      .card-label {
        font-size: 0.75rem;
        color: #6c757d;
        text-transform: uppercase;
      }
      p { margin: 0.25rem 0 0; }
    }
    .list-product {
      .card-label {
        font-size: 0.75rem;
        color: #6c757d;
        text-transform: uppercase;
        margin-bottom: 0.5rem;
        display: block;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        th, td {
          padding: 0.5rem 0.75rem;
          border: 1px solid #e7e9ed;
          font-size: 0.875rem;
        }
        th {
          background: #f1f3f5;
          font-weight: 600;
          color: #495057;
        }
        tfoot td {
          background: #f8f9fa;
        }
      }
    }
  }
}
</style>
